import type { IdZonaRebatimentoPainel } from '../../dominio/didatico/rebatimentoPainel'
import {
  secaoColuna,
  xDaEstrutura,
  xDaEstruturaNaAltura,
  zDaColunaANaAltura,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import type { IdPortaOficina } from './zonasOperacaoPorta'

/** Recuo traseiro do perfil e base do calço em relação ao ponto de ancoragem do extensor. */
export const RECUO_TRASEIRO_CALCO_EXTENSOR = 0.22

/**
 * Pose do painel trabalhado de um lado só: gira em torno da coluna A oposta, que fica parada,
 * e o lado do corte avança `avanco` e sobe `elevacao`.
 *
 * Convenção de rotação (Euler 'XYZ'):
 * - Guinada (Y): rotação em torno do eixo Y que desloca o lado trabalhado para a frente (+Z).
 * - Rolagem (Z): rotação em torno do eixo Z que levanta o lado trabalhado (+Y).
 */
export function poseDoPainelTrabalhado(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
  avanco: number,
  elevacao: number,
): { pivo: [number, number, number]; rotacao: [number, number, number] } {
  const sinal = idPorta.endsWith('Esquerda') ? 1 : -1
  const pivo: [number, number, number] = [
    xDaEstrutura(perfil, -sinal),
    perfil.cinturaY - 0.16,
    perfil.colunaA.zBase - 0.16,
  ]

  if (avanco === 0 && elevacao === 0) {
    return { pivo, rotacao: [0, 0, 0] }
  }

  const d = 2 * Math.abs(xDaEstrutura(perfil, 1))
  if (d <= 0) {
    return { pivo, rotacao: [0, 0, 0] }
  }

  const rotacaoY = -sinal * Math.asin(Math.max(-1, Math.min(1, avanco / d)))
  const rotacaoZ = sinal * Math.asin(Math.max(-1, Math.min(1, elevacao / d)))

  return {
    pivo,
    rotacao: [0, rotacaoY, rotacaoZ],
  }
}

export function pontosRebatimentoPainel(perfil: PerfilVeiculo, idPorta: IdPortaOficina, deslocamento = 0) {
  const sinal = idPorta.endsWith('Esquerda') ? 1 : -1
  const yMedio = perfil.cinturaY + (perfil.tetoY - perfil.cinturaY) * 0.35
  const yBase = perfil.topoSoleiraY + 0.07
  const coluna = (y: number, desloca = 0): [number, number, number] => [
    xDaEstruturaNaAltura(perfil, sinal, y),
    y + desloca * 0.4,
    zDaColunaANaAltura(perfil, y) + desloca,
  ]
  const topoDobradicas = (desloca = 0): [number, number, number] => [
    xDaEstruturaNaAltura(perfil, sinal, perfil.cinturaY),
    perfil.cinturaY + desloca * 0.4,
    perfil.portaDianteira.zDianteiro + desloca,
  ]
  const baseSuperiorA = (desloca = 0): [number, number, number] => [
    xDaEstruturaNaAltura(perfil, sinal, perfil.cinturaY),
    perfil.cinturaY + desloca * 0.4,
    perfil.colunaA.zBase + desloca,
  ]
  const secaoB = secaoColuna('B')
  const larguraPeB = secaoB.larguraPe ?? secaoB.largura
  const zBaseExtensor = perfil.colunaB.z + larguraPeB / 2 + RECUO_TRASEIRO_CALCO_EXTENSOR + 0.005

  return {
    sinal, yMedio, yBase, coluna, topoDobradicas, baseSuperiorA,
    corteMedio: coluna(yMedio),
    corteBase: coluna(yBase),
    baseExtensor: [
      xDaEstruturaNaAltura(perfil, sinal, perfil.topoSoleiraY) - sinal * 0.03,
      perfil.topoSoleiraY + 0.16,
      zBaseExtensor,
    ] as [number, number, number],
    cabecaExtensor: coluna(yMedio - 0.07, deslocamento),
  }
}

export function zonaRebatimentoPorId(perfil: PerfilVeiculo, idPorta: IdPortaOficina, id: IdZonaRebatimentoPainel, deslocamento = 0) {
  const pontos = pontosRebatimentoPainel(perfil, idPorta, deslocamento)
  const posicoes: Record<IdZonaRebatimentoPainel, [number, number, number]> = {
    colunaAMedia: pontos.corteMedio,
    colunaABase: pontos.corteBase,
    baseExtensor: pontos.baseExtensor,
    apoioColunaA: pontos.cabecaExtensor,
  }
  return { id, posicao: posicoes[id], anguloGraus: id === 'colunaAMedia' || id === 'colunaABase' ? 90 : 0 }
}

/** O corpo tem tamanho fixo; só as hastes mudam de curso para ligar os apoios. */
export function poseExtensorEntrePontos(base: [number, number, number], cabeca: [number, number, number]) {
  const distancia = Math.hypot(...cabeca.map((n, i) => n - base[i]))
  const escala = 1.25
  // CilindroTelescopicoHolmatro3D: garra traseira em -0,54 e frontal em 0,04 + curso.
  const progresso = Math.max(0, Math.min(1, (distancia / escala - 0.58) / 0.72))
  return { distancia, escala, progresso, offsetOrigem: 0.54 * escala }
}
